import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Checkbox,
  FormControlLabel,
  useTheme,
  alpha,
  Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import InventoryIcon from '@mui/icons-material/Inventory2';
import WarningIcon from '@mui/icons-material/Warning';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { useSnackbar } from 'notistack';

import SBDropdown from '../../../components/Dropdown';
import SbTextField from '../../../components/Textfield';
import RadioButton from '../../../components/RadioButton';
import {
  isUSDestination,
  CPSC_DISCLAIM_CODE_OPTIONS,
  CPSC_DEFAULT_DISCLAIM_CODE,
  normalizeCpscDisclaimCode,
  CPSC_PRODUCT_ID_TYPE_OPTIONS,
  validateCpscCompliance,
  buildCpscPayload,
  parseBoolean,
} from '../../../utils/usCompliance';
import Contact from '@Components/Contact';

import { ShipmentUserSchema } from './Schema';
import { getProductDescriptionSchema } from '../ShipmentDetails/ProductDescription/schema';
import { SHIPMENT_TYPE } from '@Constants/Shipment';
import { SHIPMENT_MODES, SHIPMENT_UNITS, PRODUCT_ID_TYPE_OPTIONS, INCOTERM_OPTIONS } from '../constants';
import { DropdownOptionType } from '../../../components/Dropdown/Type';
import { isDutyChargesAllowedSubdomain } from '../../../utils/getSubdomain';
import {
  resolveShipmentValueInEur,
  getEuComplianceRule,
  isEUCountry,
  resolveShipmentValue,
  isComplianceRequired,
} from '../../../utils/euCompliance';
import { useEurExchangeRate } from '../../../utils/currencyConversion';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { userLogin } from '@Services/hasAdminAccess';
import { useSelector } from 'react-redux';
import { RootStateType } from '../../../store';
import { isExportShipment } from '../../../utils/exportShipment';

interface EditBulkShipmentModalProps {
  shipment: any;
  onClose: () => void;
  onSave: (updated: any) => void;
  countryList: DropdownOptionType[];
  currencyList: DropdownOptionType[];
}

export default function EditBulkShipmentModal({
  shipment,
  onClose,
  onSave,
  countryList,
  currencyList,
}: EditBulkShipmentModalProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [tabIndex, setTabIndex] = useState(0);
  const [edited, setEdited] = useState(() => JSON.parse(JSON.stringify(shipment)));

  // Validate address using Joi
  const validateAddress = (addr: any) => {
    const cleanAddr = {
      ...addr,
      phoneNumber: addr.phoneNumber ? Number(addr.phoneNumber) : undefined,
      telephoneCode: addr.telephoneCode ? Number(addr.telephoneCode) : undefined,
    };
    const { error } = ShipmentUserSchema.validate(cleanAddr, { abortEarly: false });
    if (!error) return {};
    const errObj: { [key: string]: string } = {};
    error.details.forEach(d => {
      const key = d.path[0] as string;
      errObj[key] = d.message;
    });
    return errObj;
  };

  const destCountry = edited.consignee?.country || '';
  const isEU = isEUCountry(destCountry);
  const isUS = isUSDestination(destCountry);

  const organizationData = useSelector((state: RootStateType) => state.orgSetup.organizationPublicData);
  const organizationCountry = useSelector((state: RootStateType) => state.orgSetup.organizationData);
  const orgCountryName = organizationData?._country?.name?.trim() || organizationCountry?._country?.name?.trim() || '';

  const isExport = isExportShipment({
    orgCountryName,
    shipper: edited.shipper,
    consignee: edited.consignee,
  });

  const shipmentCurrency = edited.package?.currency || 'INR';
  const isUserLogin = userLogin();
  const complianceRequired = isComplianceRequired(isUserLogin);
  const rawShipmentValue = edited.package?.totalShipmentValue;
  const boxes = edited.package?.box || [];
  const { rate: eurRate } = useEurExchangeRate({ currency: shipmentCurrency, isEU });
  const shipmentValueEur = resolveShipmentValueInEur(boxes, shipmentCurrency, rawShipmentValue, eurRate);
  const shipmentMode = edited.package?.shipmentMode || '';
  const euRule = getEuComplianceRule({
    destCountry,
    shipmentMode,
    shipmentValueEur,
    isUserLogin,
  });
  const isCpscRequired = isUS && complianceRequired;

  const currentIncoterm = edited.package?.incoterm || 'FOB';
  const isCIF = currentIncoterm === 'CIF';
  const isCNF = currentIncoterm === 'CNF';
  const hasFreightInsurance = isCIF || isCNF;
  const isB2C = (shipmentMode || '').toUpperCase() === 'B2C';
  const isB2B = (shipmentMode || '').toUpperCase() === 'B2B';
  const showIoss = isB2C && isEU;

  const resolvedShipmentValue = resolveShipmentValue(boxes, rawShipmentValue);
  const eurEquivalentText = useMemo(() => {
    if (!isEU || !eurRate || (shipmentCurrency || '').toUpperCase() === 'EUR') return '';
    const eurVal = (resolvedShipmentValue * eurRate).toFixed(2);
    return `(≈ €${eurVal})`;
  }, [isEU, eurRate, shipmentCurrency, resolvedShipmentValue]);
  const numericFreight = parseFloat(edited.package?.freight) || 0;
  const numericInsurance = parseFloat(edited.package?.freightInsurance) || 0;
  const totalDeclaredValue =
    resolvedShipmentValue + numericFreight + (isCIF ? numericInsurance : numericInsurance || 0);

  const isFreightErr =
    hasFreightInsurance &&
    (!edited.package?.freight || isNaN(parseFloat(edited.package.freight)) || parseFloat(edited.package.freight) < 0);
  const isInsuranceErr =
    isCIF &&
    (!edited.package?.freightInsurance ||
      isNaN(parseFloat(edited.package.freightInsurance)) ||
      parseFloat(edited.package.freightInsurance) < 0);
  const isVatTaxIdErr = isB2B && !edited.package?.recipientVatTaxId?.trim();
  const isEcom = (shipmentMode || '').toUpperCase() === 'ECOM';
  const isPurposeErr =
    isEcom &&
    !(
      edited.package?.purposeOfShipment ||
      edited.package?.extraDetails?.purposeOfShipment ||
      edited.package?.extraDetails?.other?.purposeOfShipment ||
      ''
    ).trim();

  // Validate product using Joi
  const validateProduct = (prod: any) => {
    const cleanProd = {
      ...prod,
      quantity: prod.quantity ? Number(prod.quantity) : undefined,
      unitValue: prod.unitValue ? Number(prod.unitValue) : undefined,
    };
    const schema = getProductDescriptionSchema(euRule.requireEuComplianceFields, isCpscRequired);
    const { error } = schema.validate(cleanProd, { abortEarly: false });
    const errObj: { [key: string]: string } = {};
    if (error) {
      error.details.forEach(d => {
        const key = d.path[0] as string;
        errObj[key] = d.message;
      });
    }
    if (isCpscRequired) {
      const cpscVal = validateCpscCompliance(prod.cpscCompliance, true, true);
      if (!cpscVal.isValid) {
        Object.assign(errObj, cpscVal.errors);
      }
    }
    return errObj;
  };

  const shipperErrors = validateAddress(edited.shipper);
  const consigneeErrors = validateAddress(edited.consignee);

  /** Strip everything except digits from a phone number string */
  const sanitizePhoneNumber = (value: string): string => value.replace(/[^\d]/g, '');

  const handleShipperChange = (field: string, value: any) => {
    const sanitized = field === 'phoneNumber' ? sanitizePhoneNumber(String(value)) : value;
    setEdited((prev: any) => ({
      ...prev,
      shipper: { ...prev.shipper, [field]: sanitized },
    }));
  };

  const handleConsigneeChange = (field: string, value: any) => {
    const sanitized = field === 'phoneNumber' ? sanitizePhoneNumber(String(value)) : value;
    setEdited((prev: any) => {
      const updatedConsignee = { ...prev.consignee, [field]: sanitized };
      const updatedPackage = { ...prev.package };

      if (field === 'country') {
        const destC = sanitized || '';
        const bxs = updatedPackage.box || [];
        const valEur = resolveShipmentValueInEur(
          bxs,
          updatedPackage.currency || 'INR',
          updatedPackage.totalShipmentValue,
        );
        const rule = getEuComplianceRule(destC, updatedPackage.shipmentMode || '', valEur);

        if (!rule.showEuComplianceFields && bxs.length) {
          updatedPackage.box = bxs.map((b: any) => ({
            ...b,
            products: (b.products || []).map((p: any) => ({
              ...p,
              merchantProductId: '',
              standardManufacturerProductId: '',
              nonStandardManufacturerProductId: '',
            })),
          }));
        }
        if (!rule.isEU) {
          updatedPackage.iossNumber = '';
        }
        const isNowUS = isUSDestination(destC);
        if (!isNowUS && bxs.length) {
          updatedPackage.box = bxs.map((b: any) => ({
            ...b,
            products: (b.products || []).map((p: any) => {
              const updated = { ...p };
              delete updated.cpscCompliance;
              if (updated.compliance?.us) {
                delete updated.compliance.us;
              }
              return updated;
            }),
          }));
        }
      }

      return {
        ...prev,
        consignee: updatedConsignee,
        package: updatedPackage,
      };
    });
  };

  const handlePackageChange = (field: string, value: any) => {
    setEdited((prev: any) => {
      const updatedPackage = { ...prev.package, [field]: value };

      if (field === 'shipmentMode') {
        const newMode = (value || '').toUpperCase();
        if (newMode !== 'B2B') {
          updatedPackage.recipientVatTaxId = '';
        }
        if (newMode !== 'B2C') {
          updatedPackage.iossNumber = '';
        }
      } else if (field === 'incoterm') {
        if (value === 'FOB') {
          updatedPackage.freight = '';
          updatedPackage.freightInsurance = '';
        } else if (value === 'CNF') {
          updatedPackage.freightInsurance = '';
        }
      }

      // Check EU compliance fields cleanup
      const destC = prev.consignee?.country || '';
      const bxs = updatedPackage.box || [];
      const valEur = resolveShipmentValueInEur(
        bxs,
        updatedPackage.currency || 'INR',
        updatedPackage.totalShipmentValue,
      );
      const rule = getEuComplianceRule(destC, updatedPackage.shipmentMode || '', valEur);

      if (!rule.showEuComplianceFields && bxs.length) {
        updatedPackage.box = bxs.map((b: any) => ({
          ...b,
          products: (b.products || []).map((p: any) => ({
            ...p,
            merchantProductId: '',
            standardManufacturerProductId: '',
            nonStandardManufacturerProductId: '',
          })),
        }));
      }

      return {
        ...prev,
        package: updatedPackage,
      };
    });
  };

  const handleBoxChange = (boxIndex: number, field: string, value: any) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], [field]: value };

      // Update totalBoxWeight
      const totalBoxWeight = boxCopy.reduce((prevW, currW) => prevW + (parseFloat(currW.weight) || 0), 0);

      return {
        ...prev,
        package: {
          ...prev.package,
          box: boxCopy,
          totalBoxWeight,
        },
      };
    });
  };

  const handleProductChange = (boxIndex: number, prodIndex: number, field: string, value: any) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = [...boxCopy[boxIndex].products];

      prodCopy[prodIndex] = { ...prodCopy[prodIndex], [field]: value };

      // Recalculate total value
      const qty = Number(prodCopy[prodIndex].quantity) || 0;
      const val = Number(prodCopy[prodIndex].unitValue) || 0;
      prodCopy[prodIndex].totalValue = qty * val;

      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: prodCopy };
      return {
        ...prev,
        package: {
          ...prev.package,
          box: boxCopy,
        },
      };
    });
  };

  const handleProductCpscChange = (boxIndex: number, prodIndex: number, field: string, value: any) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = [...boxCopy[boxIndex].products];
      const currentProd = prodCopy[prodIndex];
      const currentCpsc = currentProd.cpscCompliance || { beingSold: true };

      let updatedCpsc = { ...currentCpsc, [field]: value };

      if (field === 'beingSold') {
        const beingSold = parseBoolean(value) ?? true;
        if (!beingSold) {
          updatedCpsc = {
            beingSold: false,
            disclaimCode: normalizeCpscDisclaimCode(currentCpsc.disclaimCode),
            additionalContext: '',
          };
        } else {
          updatedCpsc = {
            beingSold: true,
            certificateFiled: undefined,
            productIdRegistry: '',
            certificateVersionId: '',
            certifierId: '',
            productId: currentProd.productId || '',
            productIdType: currentProd.productIdType || 'SKU',
          };
        }
      } else if (field === 'certificateFiled') {
        const certificateFiled = parseBoolean(value);
        if (certificateFiled === true) {
          updatedCpsc = {
            ...currentCpsc,
            beingSold: true,
            certificateFiled: true,
            productId: '',
            productIdType: '',
          };
        } else if (certificateFiled === false) {
          updatedCpsc = {
            ...currentCpsc,
            beingSold: true,
            certificateFiled: false,
            productIdRegistry: '',
            certificateVersionId: '',
            certifierId: '',
            productId: currentCpsc.productId || currentProd.productId || '',
            productIdType: currentCpsc.productIdType || currentProd.productIdType || 'SKU',
          };
        }
      }

      prodCopy[prodIndex] = { ...currentProd, cpscCompliance: updatedCpsc };
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: prodCopy };
      return {
        ...prev,
        package: {
          ...prev.package,
          box: boxCopy,
        },
      };
    });
  };

  const addBox = () => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      boxCopy.push({
        id: boxCopy.length.toString(),
        length: '',
        width: '',
        height: '',
        weight: '',
        boxCopyCount: 0,
        products: [],
      });
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const removeBox = (boxIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = prev.package.box.filter((_: any, i: number) => i !== boxIndex);
      const reindexed = boxCopy.map((box: any, idx: number) => ({
        ...box,
        id: idx.toString(),
      }));
      return {
        ...prev,
        package: { ...prev.package, box: reindexed },
      };
    });
  };

  const addProduct = (boxIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = [...boxCopy[boxIndex].products];
      prodCopy.push({
        id: prodCopy.length.toString(),
        description: '',
        quantity: 1,
        unitValue: 1,
        HSNName: '',
        HSNCode: '',
        productId: '',
        productIdType: 'SKU',
        merchantProductId: '',
        standardManufacturerProductId: '',
        nonStandardManufacturerProductId: '',
        totalValue: 1,
      });
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: prodCopy };
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const removeProduct = (boxIndex: number, prodIndex: number) => {
    setEdited((prev: any) => {
      const boxCopy = [...prev.package.box];
      const prodCopy = boxCopy[boxIndex].products.filter((_: any, i: number) => i !== prodIndex);
      const reindexed = prodCopy.map((p: any, idx: number) => ({
        ...p,
        id: idx.toString(),
      }));
      boxCopy[boxIndex] = { ...boxCopy[boxIndex], products: reindexed };
      return {
        ...prev,
        package: { ...prev.package, box: boxCopy },
      };
    });
  };

  const handleSaveClick = () => {
    if (isVatTaxIdErr || isFreightErr || isInsuranceErr || isPurposeErr) {
      setTabIndex(2);
      if (isPurposeErr) {
        enqueueSnackbar('Purpose Of Shipment is required for ECOM shipments', { variant: 'warning' });
      }
      return;
    }

    let hasProductErrors = false;
    (edited.package?.box || []).forEach((b: any) => {
      (b.products || []).forEach((p: any) => {
        if (Object.keys(validateProduct(p)).length > 0) {
          hasProductErrors = true;
        }
      });
    });

    if (hasProductErrors) {
      setTabIndex(3);
      enqueueSnackbar('Please complete all required product details before saving.', { variant: 'warning' });
      return;
    }

    const purposeVal = (
      edited.package?.purposeOfShipment ||
      edited.package?.extraDetails?.purposeOfShipment ||
      edited.package?.extraDetails?.other?.purposeOfShipment ||
      ''
    ).trim();

    const updatedPackage = {
      ...edited.package,
      purposeOfShipment: purposeVal,
      extraDetails: {
        ...(edited.package?.extraDetails || {}),
        purposeOfShipment: purposeVal,
      },
      incoterm: edited.package?.incoterm || 'FOB',
      freight: hasFreightInsurance ? (edited.package?.freight !== undefined ? edited.package.freight : '') : '',
      freightInsurance: isCIF
        ? edited.package?.freightInsurance !== undefined
          ? edited.package.freightInsurance
          : ''
        : isCNF
        ? edited.package?.freightInsurance || ''
        : '',
      iossNumber: showIoss ? (edited.package?.iossNumber || '').trim() : '',
      recipientVatTaxId: isB2B ? (edited.package?.recipientVatTaxId || '').trim() : '',
      iorEoriNumber: (edited.package?.iorEoriNumber ?? edited.package?.ior ?? '').trim(),
      box: (edited.package?.box || []).map((b: any) => ({
        ...b,
        products: (b.products || []).map((p: any) => {
          let standardManufacturerProductId = p.standardManufacturerProductId ?? '';
          let merchantProductId = p.merchantProductId ?? '';
          let nonStandardManufacturerProductId = p.nonStandardManufacturerProductId ?? '';

          standardManufacturerProductId =
            standardManufacturerProductId && String(standardManufacturerProductId).trim() !== ''
              ? String(standardManufacturerProductId).trim()
              : 'NO';
          if (euRule.showEuComplianceFields) {
            merchantProductId = merchantProductId ? String(merchantProductId).trim() : '';
            nonStandardManufacturerProductId = nonStandardManufacturerProductId
              ? String(nonStandardManufacturerProductId).trim()
              : '';
          } else {
            merchantProductId = '';
            nonStandardManufacturerProductId = '';
          }

          const rawCpsc = p.cpscCompliance || p.compliance?.us;
          const cpscCompliance = isUS && rawCpsc ? buildCpscPayload(rawCpsc, true) : undefined;

          return {
            ...p,
            merchantProductId,
            standardManufacturerProductId,
            nonStandardManufacturerProductId,
            ...(cpscCompliance ? { cpscCompliance } : {}),
            compliance: {
              ...(p.compliance || {}),
              ...(cpscCompliance ? { us: cpscCompliance } : {}),
            },
          };
        }),
      })),
    };

    onSave({
      ...edited,
      package: updatedPackage,
    });
  };

  const theme = useTheme();

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 12px 48px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 2,
          px: 3,
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <AutoAwesomeIcon sx={{ color: theme.palette.primary.main, fontSize: '1.4rem' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, fontSize: '1.15rem' }}>
            Edit Shipment Details
          </Typography>
          {edited.package?.invoiceNumber && (
            <Chip
              label={edited.package.invoiceNumber}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 700, fontSize: '0.75rem', height: '24px' }}
            />
          )}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.palette.text.secondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: theme.palette.background.paper }}>
        <Tabs
          value={tabIndex}
          onChange={(_, val) => setTabIndex(val)}
          aria-label="shipment edit tabs"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.875rem',
              minHeight: '48px',
            },
          }}
        >
          <Tab
            icon={<PersonIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Shipper Address"
          />
          <Tab
            icon={<LocalShippingIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Consignee Address"
          />
          <Tab
            icon={<InventoryIcon sx={{ fontSize: '18px !important' }} />}
            iconPosition="start"
            label="Package & Boxes"
          />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, maxHeight: '65vh', overflowY: 'auto' }}>
        {edited.reviewReason && (
          <Box
            sx={{
              mb: 2.5,
              p: 2,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.warning.main, 0.08),
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
            }}
          >
            <WarningIcon sx={{ color: theme.palette.warning.main, fontSize: '1.25rem', mt: 0.2 }} />
            <Box>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.warning.dark || '#b78103',
                  display: 'block',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                AI Review Notice
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, mt: 0.25 }}>
                {edited.reviewReason}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Shipper Tab */}
        {tabIndex === 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
                Shipper Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Name"
                label="Shipper Name"
                required
                name="name"
                value={edited.shipper?.name || ''}
                onChange={e => handleShipperChange('name', e.target.value)}
                error={shipperErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Company Name"
                label="Company Name"
                name="companyName"
                value={edited.shipper?.companyName || ''}
                onChange={e => handleShipperChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Contact
                label="Mobile No. *"
                phoneName="phoneNumber"
                phoneNumber={edited.shipper?.phoneNumber || ''}
                onChangePhoneNumber={e => handleShipperChange('phoneNumber', e.target.value)}
                telCountryCodeName="telephoneCode"
                telCountryCode={edited.shipper?.telephoneCode || ''}
                onChangeTelCountryCode={event => handleShipperChange('telephoneCode', event.target.value as string)}
                setDefaultTelephone={value => handleShipperChange('telephoneCode', value.toString())}
                error={shipperErrors.phoneNumber || shipperErrors.telephoneCode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Email Id"
                label="Email Address"
                name="email"
                value={edited.shipper?.email || ''}
                onChange={e => handleShipperChange('email', e.target.value)}
                error={shipperErrors.email || shipperErrors.emailFormat}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 1"
                label="Address Line 1"
                required
                name="line1"
                value={edited.shipper?.line1 || ''}
                onChange={e => handleShipperChange('line1', e.target.value)}
                error={shipperErrors.line1}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 2"
                label="Address Line 2"
                name="line2"
                value={edited.shipper?.line2 || ''}
                onChange={e => handleShipperChange('line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="City"
                label="City"
                required
                name="city"
                value={edited.shipper?.city || ''}
                onChange={e => handleShipperChange('city', e.target.value)}
                error={shipperErrors.city}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="State"
                label="State"
                name="state"
                value={edited.shipper?.state || ''}
                onChange={e => handleShipperChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="Pincode"
                label="Pincode"
                required
                name="pincode"
                value={edited.shipper?.pincode || ''}
                onChange={e => handleShipperChange('pincode', e.target.value)}
                error={shipperErrors.pincode}
              />
            </Grid>
            <Grid item xs={12}>
              <SBDropdown
                id="shipper-country-id"
                label="Country"
                required
                name="country"
                placeholder="Country"
                options={countryList}
                value={edited.shipper?.country ? [edited.shipper.country] : []}
                onChange={selected => handleShipperChange('country', selected[0] || '')}
                error={shipperErrors.country}
              />
            </Grid>
          </Grid>
        )}

        {/* Consignee Tab */}
        {tabIndex === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
                Consignee Information
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Name"
                label="Consignee Name"
                required
                name="name"
                value={edited.consignee?.name || ''}
                onChange={e => handleConsigneeChange('name', e.target.value)}
                error={consigneeErrors.name}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Company Name"
                label="Company Name"
                name="companyName"
                value={edited.consignee?.companyName || ''}
                onChange={e => handleConsigneeChange('companyName', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Contact
                label="Mobile No. *"
                phoneName="phoneNumber"
                phoneNumber={edited.consignee?.phoneNumber || ''}
                onChangePhoneNumber={e => handleConsigneeChange('phoneNumber', e.target.value)}
                telCountryCodeName="telephoneCode"
                telCountryCode={edited.consignee?.telephoneCode || ''}
                onChangeTelCountryCode={event => handleConsigneeChange('telephoneCode', event.target.value as string)}
                setDefaultTelephone={value => handleConsigneeChange('telephoneCode', value.toString())}
                error={consigneeErrors.phoneNumber || consigneeErrors.telephoneCode}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <SbTextField
                placeholder="Enter Email Id"
                label="Email Address"
                name="email"
                value={edited.consignee?.email || ''}
                onChange={e => handleConsigneeChange('email', e.target.value)}
                error={consigneeErrors.email || consigneeErrors.emailFormat}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 1"
                label="Address Line 1"
                required
                name="line1"
                value={edited.consignee?.line1 || ''}
                onChange={e => handleConsigneeChange('line1', e.target.value)}
                error={consigneeErrors.line1}
              />
            </Grid>
            <Grid item xs={12}>
              <SbTextField
                placeholder="Line 2"
                label="Address Line 2"
                name="line2"
                value={edited.consignee?.line2 || ''}
                onChange={e => handleConsigneeChange('line2', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="City"
                label="City"
                required
                name="city"
                value={edited.consignee?.city || ''}
                onChange={e => handleConsigneeChange('city', e.target.value)}
                error={consigneeErrors.city}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="State"
                label="State"
                name="state"
                value={edited.consignee?.state || ''}
                onChange={e => handleConsigneeChange('state', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <SbTextField
                placeholder="Pincode"
                label="Pincode"
                required
                name="pincode"
                value={edited.consignee?.pincode || ''}
                onChange={e => handleConsigneeChange('pincode', e.target.value)}
                error={consigneeErrors.pincode}
              />
            </Grid>
            <Grid item xs={12}>
              <SBDropdown
                id="consignee-country-id"
                label="Country"
                required
                name="country"
                placeholder="Country"
                options={countryList}
                value={edited.consignee?.country ? [edited.consignee.country] : []}
                onChange={selected => handleConsigneeChange('country', selected[0] || '')}
                error={consigneeErrors.country}
              />
            </Grid>
          </Grid>
        )}

        {/* Package & Boxes Tab */}
        {tabIndex === 2 && (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, color: '#666' }}>
              Package Parameters
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="unitOfMeasurement"
                  label="Unit of Measurement"
                  required
                  name="unitOfMeasurement"
                  placeholder="Select Unit"
                  options={SHIPMENT_UNITS}
                  value={edited.package?.unitOfMeasurement ? [edited.package.unitOfMeasurement] : []}
                  onChange={selected => handlePackageChange('unitOfMeasurement', selected[0] || '')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="currency"
                  label="Currency"
                  required
                  name="currency"
                  placeholder="Select Currency"
                  options={currencyList}
                  value={edited.package?.currency ? [edited.package.currency] : []}
                  onChange={selected => handlePackageChange('currency', selected[0] || '')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="shipmentMode"
                  label="Shipment Mode"
                  required
                  name="shipmentMode"
                  placeholder="Select Mode"
                  options={SHIPMENT_MODES}
                  value={edited.package?.shipmentMode ? [edited.package.shipmentMode] : []}
                  onChange={selected => handlePackageChange('shipmentMode', selected[0] || '')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="incoterm"
                  label="Incoterm"
                  required
                  name="incoterm"
                  placeholder="Select Incoterm"
                  options={INCOTERM_OPTIONS}
                  value={edited.package?.incoterm ? [edited.package.incoterm] : ['FOB']}
                  onChange={selected => handlePackageChange('incoterm', selected[0] || 'FOB')}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SBDropdown
                  id="purposeOfShipment"
                  label={`Purpose Of Shipment${(shipmentMode || '').toUpperCase() === 'ECOM' ? ' *' : ''}`}
                  required={(shipmentMode || '').toUpperCase() === 'ECOM'}
                  name="purposeOfShipment"
                  placeholder="Select Purpose"
                  options={SHIPMENT_TYPE}
                  value={
                    edited.package?.purposeOfShipment || edited.package?.extraDetails?.purposeOfShipment
                      ? [edited.package?.purposeOfShipment || edited.package?.extraDetails?.purposeOfShipment]
                      : []
                  }
                  onChange={selected => {
                    const val = selected[0] || '';
                    handlePackageChange('purposeOfShipment', val);
                    if (edited.package?.extraDetails) {
                      handlePackageChange('extraDetails', { ...edited.package.extraDetails, purposeOfShipment: val });
                    }
                  }}
                  error={isPurposeErr ? 'Required for ECOM shipments' : ''}
                />
              </Grid>
              {isB2B && (
                <Grid item xs={12} md={6}>
                  <SbTextField
                    placeholder="Enter Recipient VAT / Tax ID"
                    label="Recipient VAT / Tax ID"
                    required
                    name="recipientVatTaxId"
                    value={edited.package?.recipientVatTaxId || ''}
                    onChange={e => handlePackageChange('recipientVatTaxId', e.target.value)}
                    error={isVatTaxIdErr ? 'Required for B2B shipments' : ''}
                  />
                </Grid>
              )}
              {showIoss && (
                <Grid item xs={12} md={6}>
                  <SbTextField
                    placeholder="Enter IOSS Number"
                    label="IOSS Number"
                    name="iossNumber"
                    value={edited.package?.iossNumber || ''}
                    onChange={e => handlePackageChange('iossNumber', e.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <SbTextField
                  placeholder="Enter IOR or EORI No."
                  label="IOR / EORI No."
                  name="iorEoriNumber"
                  value={edited.package?.iorEoriNumber ?? edited.package?.ior ?? ''}
                  onChange={e => {
                    handlePackageChange('iorEoriNumber', e.target.value);
                    handlePackageChange('ior', e.target.value);
                  }}
                />
              </Grid>
              {isDutyChargesAllowedSubdomain() && (
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 0.75 }}>
                      Duty Charge
                    </Typography>
                    {(() => {
                      const currentDuty =
                        (edited.package?.dutyCharge
                          ? typeof edited.package.dutyCharge === 'string'
                            ? edited.package.dutyCharge
                            : edited.package.dutyCharge.type || ''
                          : '') || (isExport ? 'DDU' : '');
                      return (
                        <Box>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              p: 0.5,
                              bgcolor: alpha(theme.palette.divider, 0.08),
                              borderRadius: '8px',
                              gap: 1,
                            }}
                          >
                            <Button
                              variant={currentDuty === 'DDU' ? 'contained' : 'text'}
                              size="small"
                              disabled={!isExport}
                              onClick={() => handlePackageChange('dutyCharge', 'DDU')}
                              sx={{
                                px: 2.5,
                                py: 0.5,
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                                bgcolor: currentDuty === 'DDU' ? 'primary.main' : 'transparent',
                                color: currentDuty === 'DDU' ? 'common.white' : 'text.primary',
                              }}
                            >
                              DDU
                            </Button>
                            <Button
                              variant={currentDuty === 'DDP' ? 'contained' : 'text'}
                              size="small"
                              disabled={!isExport}
                              onClick={() => handlePackageChange('dutyCharge', 'DDP')}
                              sx={{
                                px: 2.5,
                                py: 0.5,
                                borderRadius: '6px',
                                fontWeight: 700,
                                fontSize: '0.8125rem',
                                bgcolor: currentDuty === 'DDP' ? 'primary.main' : 'transparent',
                                color: currentDuty === 'DDP' ? 'common.white' : 'text.primary',
                              }}
                            >
                              DDP
                            </Button>
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5 }}>
                            {currentDuty === 'DDP' ? 'Added to your charges' : 'Consignee pays on delivery'}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </Box>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <SbTextField
                  placeholder="Invoice Number"
                  label="Invoice Number"
                  name="invoiceNumber"
                  value={edited.package?.invoiceNumber || ''}
                  onChange={e => handlePackageChange('invoiceNumber', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SbTextField
                  placeholder="Reference Number"
                  label="Reference Number"
                  name="userReferenceNumber"
                  value={edited.package?.userReferenceNumber || ''}
                  onChange={e => handlePackageChange('userReferenceNumber', e.target.value)}
                />
              </Grid>
            </Grid>

            {hasFreightInsurance && (
              <Box
                sx={{
                  mb: 3,
                  p: 2.5,
                  borderRadius: '12px',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <PaymentsOutlinedIcon color="primary" fontSize="small" />
                  <Typography sx={{ fontSize: '16px', fontWeight: 500, color: 'primary.main' }}>
                    Freight & Insurance ({currentIncoterm})
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                  {isCIF
                    ? 'CIF Incoterm includes both Freight and Insurance charges in the declaration.'
                    : 'CNF Incoterm includes Freight charges; Insurance is optional.'}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <SbTextField
                      required
                      type="number"
                      label={`Freight (${shipmentCurrency})`}
                      placeholder="Enter freight amount"
                      name="freight"
                      value={edited.package?.freight !== undefined ? edited.package.freight : ''}
                      onChange={e => handlePackageChange('freight', e.target.value)}
                      error={isFreightErr ? 'Freight amount is required' : ''}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <SbTextField
                      required={isCIF}
                      type="number"
                      label={`Insurance (${shipmentCurrency})${isCIF ? '' : ' (Optional)'}`}
                      placeholder="Enter insurance amount"
                      name="freightInsurance"
                      value={edited.package?.freightInsurance !== undefined ? edited.package.freightInsurance : ''}
                      onChange={e => handlePackageChange('freightInsurance', e.target.value)}
                      error={isInsuranceErr ? 'Insurance amount is required for CIF' : ''}
                    />
                  </Grid>
                </Grid>

                <Box
                  sx={{
                    mt: 2.5,
                    p: 2,
                    borderRadius: '10px',
                    bgcolor: theme.palette.background.paper,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                  }}
                >
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1.5 }}>
                    Price Breakup
                  </Typography>
                  <Divider sx={{ mb: 2, borderColor: alpha(theme.palette.divider, 0.08) }} />
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Shipment Value (Goods)
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mt: 0.5 }}>
                        {shipmentCurrency} {resolvedShipmentValue.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Freight Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: numericFreight > 0 ? 'text.primary' : 'text.secondary', mt: 0.5 }}
                      >
                        {shipmentCurrency} {numericFreight.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.secondary', display: 'block' }}>
                        Insurance Amount
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: numericInsurance > 0 ? 'text.primary' : 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {shipmentCurrency} {numericInsurance.toFixed(2)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'primary.main', display: 'block' }}>
                        Total Declared Value
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.main', mt: 0.5 }}>
                        {shipmentCurrency} {totalDeclaredValue.toFixed(2)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Packaging and Insurance */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!edited.package?.packaging}
                      onChange={e => handlePackageChange('packaging', e.target.checked)}
                    />
                  }
                  label="Add Packaging"
                />
              </Grid>
              <Grid item>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!edited.package?.insurance}
                      onChange={e => handlePackageChange('insurance', e.target.checked)}
                    />
                  }
                  label="Insure Your Shipment"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#666' }}>
                Boxes & Dimensions
              </Typography>
              <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addBox}>
                Add Box
              </Button>
            </Box>

            {edited.package?.box?.map((box: any, boxIdx: number) => {
              const boxLengthErr = isNaN(parseFloat(box.length)) || parseFloat(box.length) <= 0;
              const boxWidthErr = isNaN(parseFloat(box.width)) || parseFloat(box.width) <= 0;
              const boxHeightErr = isNaN(parseFloat(box.height)) || parseFloat(box.height) <= 0;
              const boxWeightErr = isNaN(parseFloat(box.weight)) || parseFloat(box.weight) <= 0;

              return (
                <Box
                  key={box.id || boxIdx}
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                    bgcolor: '#fafafa',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333' }}>
                      Box {boxIdx + 1}
                    </Typography>
                    {edited.package.box.length > 1 && (
                      <IconButton color="error" size="small" onClick={() => removeBox(boxIdx)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Length"
                        label="Length"
                        required
                        type="number"
                        name="length"
                        value={box.length || ''}
                        onChange={e => handleBoxChange(boxIdx, 'length', e.target.value)}
                        error={boxLengthErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Width"
                        label="Width"
                        required
                        type="number"
                        name="width"
                        value={box.width || ''}
                        onChange={e => handleBoxChange(boxIdx, 'width', e.target.value)}
                        error={boxWidthErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Height"
                        label="Height"
                        required
                        type="number"
                        name="height"
                        value={box.height || ''}
                        onChange={e => handleBoxChange(boxIdx, 'height', e.target.value)}
                        error={boxHeightErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SbTextField
                        placeholder="Weight"
                        label="Weight"
                        required
                        type="number"
                        name="weight"
                        value={box.weight || ''}
                        onChange={e => handleBoxChange(boxIdx, 'weight', e.target.value)}
                        error={boxWeightErr ? 'Required > 0' : ''}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#555' }}>
                      Products in Box {boxIdx + 1}
                    </Typography>
                    <Button startIcon={<AddIcon />} variant="text" size="small" onClick={() => addProduct(boxIdx)}>
                      Add Product
                    </Button>
                  </Box>

                  {box.products?.map((prod: any, prodIdx: number) => {
                    const prodErrors = validateProduct(prod);
                    const isProdDescErr = !!prodErrors.description;
                    const isProdQtyErr = !!prodErrors.quantity;
                    const isProdValErr = !!prodErrors.unitValue;
                    const isHSNErr = isExport && !prod.HSNCode?.trim();

                    return (
                      <Box
                        key={prod.id || prodIdx}
                        sx={{
                          border: '1px dashed #ccc',
                          borderRadius: '6px',
                          p: 1.5,
                          mb: 2,
                          bgcolor: '#ffffff',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#777' }}>
                            Product {prodIdx + 1}
                          </Typography>
                          {box.products.length > 1 && (
                            <IconButton color="error" size="small" onClick={() => removeProduct(boxIdx, prodIdx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>

                        <Grid container spacing={1.5}>
                          <Grid item xs={12} md={4}>
                            <SbTextField
                              placeholder="Description"
                              label="Description"
                              required
                              name="description"
                              value={prod.description || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'description', e.target.value)}
                              error={isProdDescErr ? prodErrors.description : ''}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SbTextField
                              placeholder="Quantity"
                              label="Quantity"
                              required
                              type="number"
                              name="quantity"
                              value={prod.quantity || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'quantity', e.target.value)}
                              error={isProdQtyErr ? prodErrors.quantity : ''}
                            />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <SbTextField
                              placeholder="Unit Value"
                              label="Unit Value"
                              required
                              type="number"
                              name="unitValue"
                              value={prod.unitValue || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'unitValue', e.target.value)}
                              error={isProdValErr ? prodErrors.unitValue : ''}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <SbTextField
                              placeholder="HSN Code"
                              label="HSN Code"
                              required={isExport}
                              name="HSNCode"
                              value={prod.HSNCode || ''}
                              onChange={e => handleProductChange(boxIdx, prodIdx, 'HSNCode', e.target.value)}
                              error={isHSNErr ? 'Required for export' : ''}
                            />
                          </Grid>
                          {(euRule.showEuComplianceFields || isUS) && (
                            <>
                              <Grid item xs={12} md={6}>
                                <SbTextField
                                  placeholder="Product ID"
                                  label="Product ID"
                                  name="productId"
                                  value={prod.productId || ''}
                                  onChange={e => {
                                    const val = e.target.value;
                                    handleProductChange(boxIdx, prodIdx, 'productId', val);
                                    if (prod.isSameAsProductId) {
                                      handleProductChange(boxIdx, prodIdx, 'merchantProductId', val);
                                    }
                                  }}
                                  error={prodErrors.productId || ''}
                                />
                              </Grid>
                              <Grid item xs={12} md={6}>
                                <SBDropdown
                                  id={`productIdType-${boxIdx}-${prodIdx}`}
                                  label="Product ID Type"
                                  name="productIdType"
                                  value={prod.productIdType ? [prod.productIdType] : ['SKU']}
                                  options={PRODUCT_ID_TYPE_OPTIONS}
                                  onChange={selected =>
                                    handleProductChange(boxIdx, prodIdx, 'productIdType', selected[0] || 'SKU')
                                  }
                                  error={prodErrors.productIdType || ''}
                                />
                              </Grid>
                            </>
                          )}
                          {euRule.showEuComplianceFields && (
                            <>
                              <Grid item xs={12}>
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: '8px',
                                    bgcolor: alpha(theme.palette.info.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.info.main, 0.15)}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    mb: 1.5,
                                  }}
                                >
                                  <InfoOutlinedIcon sx={{ color: 'info.main', fontSize: 18 }} />
                                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary' }}>
                                    Goods value {shipmentCurrency} {resolvedShipmentValue.toFixed(2)}
                                    {eurEquivalentText ? ` ${eurEquivalentText}` : ''} · €3 duty per line
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      size="small"
                                      checked={
                                        !!prod.isSameAsProductId ||
                                        (!!prod.productId && prod.productId === prod.merchantProductId)
                                      }
                                      onChange={e => {
                                        const checked = e.target.checked;
                                        handleProductChange(boxIdx, prodIdx, 'isSameAsProductId', checked);
                                        if (checked) {
                                          handleProductChange(
                                            boxIdx,
                                            prodIdx,
                                            'merchantProductId',
                                            prod.productId || '',
                                          );
                                        }
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Same as Product ID
                                    </Typography>
                                  }
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <SbTextField
                                  placeholder="Merchant ID"
                                  label={`Merchant ID (M-PID)${euRule.requireEuComplianceFields ? ' *' : ''}`}
                                  required={euRule.requireEuComplianceFields}
                                  name="merchantProductId"
                                  value={prod.merchantProductId || ''}
                                  onChange={e =>
                                    handleProductChange(boxIdx, prodIdx, 'merchantProductId', e.target.value)
                                  }
                                  error={prodErrors.merchantProductId || ''}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <SbTextField
                                  placeholder="Barcode"
                                  label="Barcode (S-PID)"
                                  name="standardManufacturerProductId"
                                  value={prod.standardManufacturerProductId || ''}
                                  onChange={e =>
                                    handleProductChange(
                                      boxIdx,
                                      prodIdx,
                                      'standardManufacturerProductId',
                                      e.target.value,
                                    )
                                  }
                                  error={prodErrors.standardManufacturerProductId || ''}
                                />
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <SbTextField
                                  placeholder="Manufacturer Part No."
                                  label={`Manufacturer part no. (NS-PID)${
                                    euRule.requireEuComplianceFields ? ' *' : ''
                                  }`}
                                  required={euRule.requireEuComplianceFields}
                                  name="nonStandardManufacturerProductId"
                                  value={prod.nonStandardManufacturerProductId || ''}
                                  onChange={e =>
                                    handleProductChange(
                                      boxIdx,
                                      prodIdx,
                                      'nonStandardManufacturerProductId',
                                      e.target.value,
                                    )
                                  }
                                  error={prodErrors.nonStandardManufacturerProductId || ''}
                                />
                              </Grid>
                            </>
                          )}

                          {isUS && (
                            <>
                              <Grid item xs={12}>
                                <Divider sx={{ my: 1.5 }} />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    color: 'primary.main',
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.5px',
                                    display: 'block',
                                    mb: 1,
                                  }}
                                >
                                  US CPSC Compliance
                                </Typography>
                              </Grid>

                              <Grid item xs={12}>
                                <Box sx={{ mb: 1 }}>
                                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: 'text.primary', mb: 1 }}>
                                    Is this item being sold?{isCpscRequired ? ' *' : ''}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'inline-flex',
                                      p: 0.5,
                                      bgcolor: alpha(theme.palette.divider, 0.08),
                                      borderRadius: '10px',
                                      gap: 1,
                                    }}
                                  >
                                    <Button
                                      variant={prod.cpscCompliance?.beingSold ? 'contained' : 'text'}
                                      onClick={() => handleProductCpscChange(boxIdx, prodIdx, 'beingSold', true)}
                                      sx={{
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        bgcolor: prod.cpscCompliance?.beingSold ? 'primary.main' : 'transparent',
                                        color: prod.cpscCompliance?.beingSold ? 'common.white' : 'text.primary',
                                        boxShadow: prod.cpscCompliance?.beingSold
                                          ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                          : 'none',
                                        '&:hover': {
                                          bgcolor: prod.cpscCompliance?.beingSold
                                            ? 'primary.dark'
                                            : alpha(theme.palette.action.hover, 0.1),
                                        },
                                      }}
                                    >
                                      Yes
                                    </Button>
                                    <Button
                                      variant={!prod.cpscCompliance?.beingSold ? 'contained' : 'text'}
                                      onClick={() => handleProductCpscChange(boxIdx, prodIdx, 'beingSold', false)}
                                      sx={{
                                        px: 3,
                                        py: 0.75,
                                        borderRadius: '8px',
                                        fontWeight: 600,
                                        fontSize: '0.875rem',
                                        bgcolor: !prod.cpscCompliance?.beingSold ? 'primary.main' : 'transparent',
                                        color: !prod.cpscCompliance?.beingSold ? 'common.white' : 'text.primary',
                                        boxShadow: !prod.cpscCompliance?.beingSold
                                          ? `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                          : 'none',
                                        '&:hover': {
                                          bgcolor: !prod.cpscCompliance?.beingSold
                                            ? 'primary.dark'
                                            : alpha(theme.palette.action.hover, 0.1),
                                        },
                                      }}
                                    >
                                      No
                                    </Button>
                                  </Box>
                                  {prodErrors.cpscBeingSold && (
                                    <Typography sx={{ color: 'error.main', fontSize: '12px', mt: 0.5 }}>
                                      {prodErrors.cpscBeingSold}
                                    </Typography>
                                  )}
                                </Box>
                              </Grid>

                              {!prod.cpscCompliance?.beingSold && (
                                <>
                                  <Grid item xs={12} md={6}>
                                    <SBDropdown
                                      id={`cpscDisclaimCode-${boxIdx}-${prodIdx}`}
                                      label="Disclaim Code"
                                      required={isCpscRequired}
                                      name="cpscDisclaimCode"
                                      placeholder="Select Disclaim Code"
                                      options={CPSC_DISCLAIM_CODE_OPTIONS}
                                      value={
                                        prod.cpscCompliance?.disclaimCode
                                          ? [normalizeCpscDisclaimCode(prod.cpscCompliance.disclaimCode)]
                                          : [CPSC_DEFAULT_DISCLAIM_CODE]
                                      }
                                      onChange={selected =>
                                        handleProductCpscChange(
                                          boxIdx,
                                          prodIdx,
                                          'disclaimCode',
                                          selected[0] || CPSC_DEFAULT_DISCLAIM_CODE,
                                        )
                                      }
                                      error={prodErrors.cpscDisclaimCode}
                                    />
                                  </Grid>
                                  <Grid item xs={12} md={6}>
                                    <SbTextField
                                      placeholder="Additional Context (optional)"
                                      label="Additional Context"
                                      name="cpscAdditionalContext"
                                      value={prod.cpscCompliance?.additionalContext || ''}
                                      onChange={e =>
                                        handleProductCpscChange(boxIdx, prodIdx, 'additionalContext', e.target.value)
                                      }
                                      error={prodErrors.cpscAdditionalContext}
                                    />
                                  </Grid>
                                </>
                              )}

                              {prod.cpscCompliance?.beingSold && (
                                <>
                                  <Grid item xs={12}>
                                    <RadioButton
                                      label="Is the certificate filed in the CPSC Product Registry?"
                                      required={isCpscRequired}
                                      name={`cpscCertificateFiled-${boxIdx}-${prodIdx}`}
                                      value={
                                        prod.cpscCompliance?.certificateFiled === true
                                          ? 'true'
                                          : prod.cpscCompliance?.certificateFiled === false
                                          ? 'false'
                                          : ''
                                      }
                                      list={[
                                        { label: 'Yes', value: 'true' },
                                        { label: 'No', value: 'false' },
                                      ]}
                                      onChange={e =>
                                        handleProductCpscChange(
                                          boxIdx,
                                          prodIdx,
                                          'certificateFiled',
                                          e.target.value === 'true',
                                        )
                                      }
                                      error={prodErrors.cpscCertificateFiled}
                                    />
                                  </Grid>

                                  {prod.cpscCompliance?.certificateFiled === true && (
                                    <>
                                      <Grid item xs={12} md={4}>
                                        <SbTextField
                                          required={isCpscRequired}
                                          placeholder="Product ID (registry)"
                                          label={`Product ID (registry)${isCpscRequired ? ' *' : ''}`}
                                          name="cpscProductIdRegistry"
                                          value={prod.cpscCompliance?.productIdRegistry || ''}
                                          onChange={e =>
                                            handleProductCpscChange(
                                              boxIdx,
                                              prodIdx,
                                              'productIdRegistry',
                                              e.target.value,
                                            )
                                          }
                                          error={prodErrors.cpscProductIdRegistry}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <SbTextField
                                          required={isCpscRequired}
                                          placeholder="Certificate Version ID"
                                          label={`Certificate Version ID${isCpscRequired ? ' *' : ''}`}
                                          name="cpscCertificateVersionId"
                                          value={prod.cpscCompliance?.certificateVersionId || ''}
                                          onChange={e =>
                                            handleProductCpscChange(
                                              boxIdx,
                                              prodIdx,
                                              'certificateVersionId',
                                              e.target.value,
                                            )
                                          }
                                          error={prodErrors.cpscCertificateVersionId}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={4}>
                                        <SbTextField
                                          required={isCpscRequired}
                                          placeholder="Certifier ID"
                                          label={`Certifier ID${isCpscRequired ? ' *' : ''}`}
                                          name="cpscCertifierId"
                                          value={prod.cpscCompliance?.certifierId || ''}
                                          onChange={e =>
                                            handleProductCpscChange(boxIdx, prodIdx, 'certifierId', e.target.value)
                                          }
                                          error={prodErrors.cpscCertifierId}
                                        />
                                      </Grid>
                                    </>
                                  )}

                                  {prod.cpscCompliance?.certificateFiled === false && (
                                    <>
                                      <Grid item xs={12} md={6}>
                                        <SbTextField
                                          required={isCpscRequired}
                                          placeholder="Product ID"
                                          label={`Product ID${isCpscRequired ? ' *' : ''}`}
                                          name="cpscProductId"
                                          value={prod.cpscCompliance?.productId || ''}
                                          onChange={e =>
                                            handleProductCpscChange(boxIdx, prodIdx, 'productId', e.target.value)
                                          }
                                          error={prodErrors.cpscProductId}
                                        />
                                      </Grid>
                                      <Grid item xs={12} md={6}>
                                        <SBDropdown
                                          id={`cpscProductIdType-${boxIdx}-${prodIdx}`}
                                          label="Product ID Type"
                                          required={isCpscRequired}
                                          name="cpscProductIdType"
                                          placeholder="Select Product ID Type"
                                          options={CPSC_PRODUCT_ID_TYPE_OPTIONS}
                                          value={
                                            prod.cpscCompliance?.productIdType
                                              ? [prod.cpscCompliance.productIdType]
                                              : ['SKU']
                                          }
                                          onChange={selected =>
                                            handleProductCpscChange(
                                              boxIdx,
                                              prodIdx,
                                              'productIdType',
                                              selected[0] || 'SKU',
                                            )
                                          }
                                          error={prodErrors.cpscProductIdType}
                                        />
                                      </Grid>
                                    </>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    );
                  })}
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: `1px solid ${theme.palette.divider}`,
          p: 2.5,
          px: 3,
          justifyContent: 'space-between',
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ borderRadius: '8px', px: 2.5, textTransform: 'none', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSaveClick}
          variant="contained"
          color="primary"
          sx={{
            borderRadius: '8px',
            px: 3.5,
            fontWeight: 700,
            textTransform: 'none',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
          }}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
